import axios from 'axios';
import { acquireAccessToken } from '../msal';
import msalInstance from '../msal';
import appConfig from '../AppConfig';
import { validateSession, handleSessionExpiry } from '../utils/sessionGuard';

const api = axios.create({
    baseURL: appConfig.apiBaseUrl,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    }
});

/**
 * REQUEST interceptor – attaches a fresh Bearer token to every outgoing
 * request.  If the token cannot be acquired the request is rejected,
 * which the response interceptor below will catch.
 *
 * Skipped for retried requests (_sessionRetried) whose token was already
 * refreshed by the response interceptor.
 */
api.interceptors.request.use(async (config) => {
    // Skip token acquisition for retried requests – the response interceptor
    // already attached the refreshed token.
    if ((config as any)._sessionRetried) return config;

    try {
        const accessToken = await acquireAccessToken({ scopes: appConfig.azureAd.accessScopes });
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    } catch (error) {
        // Token acquisition failed (e.g. no MSAL accounts) — session is dead.
        // Clean up and redirect before rejecting.
        await handleSessionExpiry(msalInstance);
        window.location.href = '/login';
        return Promise.reject(error);
    }
});

/**
 * RESPONSE interceptor – handles 401 Unauthorized globally.
 *
 * On a 401 the interceptor:
 *  1. Attempts to revalidate / extend the session silently.
 *  2. If the session is still valid the original request is retried once
 *     with the refreshed token.
 *  3. If the session is invalid the user is logged out and redirected to
 *     `/login`.
 */
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Request was aborted (e.g. component unmount, effect cleanup, superseded
        // request) — this is expected control flow, not a real error, so skip
        // logging and all session/retry handling below.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._sessionRetried) {
            originalRequest._sessionRetried = true;

            const detail = error.response?.data?.detail;
            const SESSION_TERMINAL_CODES = ['session_ended', 'session_inactive', 'session_max_duration'];

            if (SESSION_TERMINAL_CODES.includes(detail)) {
                // Server explicitly ended session — no retry possible
                await handleSessionExpiry(msalInstance);
                window.location.href = '/login';
                return Promise.reject(error);
            }

            const result = await validateSession(msalInstance, appConfig.azureAd.accessScopes);
            if (result.valid && result.accessToken) {
                // Session was extended – retry the original request with the new token
                originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
                return api(originalRequest);
            }

            // Session cannot be recovered – force logout
            await handleSessionExpiry(msalInstance);
            localStorage.removeItem('user');
            localStorage.removeItem('token');

            // Navigate to login (works outside React tree)
            window.location.href = '/login';
            return Promise.reject(error);
        }

        console.error('API response error:', error);
        return Promise.reject(error);
    },
);

export default api;